// Application constants

// API Configuration
export const API_ENDPOINTS = {
  TEAMS: '/teams',
  PLAYERS: '/players',
  PRACTICES: '/practices',
  DRILLS: '/drills',
  DEPTH_CHART: '/depth-chart',
  FORMATIONS: '/formations'
} as const;

// Validation Constants
export const VALIDATION_LIMITS = {
  PLAYER_NUMBER: { MIN: 0, MAX: 999 },
  PLAYER_WEIGHT: { MIN: 50, MAX: 500 },
  PLAYER_SPEED: { MIN: 1, MAX: 10 },
  PLAYER_STRENGTH: { MIN: 1, MAX: 10 },
  ROSTER_SIZE: { MIN: 1, MAX: 100 },
  DRILL_DURATION: { MIN: 1, MAX: 120 },
  TEAM_NAME_LENGTH: { MIN: 1, MAX: 100 },
  PLAYER_NAME_LENGTH: { MIN: 1, MAX: 50 }
} as const;

// Time Constants
export const TIME_FORMATS = {
  TWELVE_HOUR: '12h',
  TWENTY_FOUR_HOUR: '24h'
} as const;

export const DEFAULT_TIMES = {
  PRACTICE_START: '15:00',
  PRACTICE_END: '17:00',
  PRACTICE_DURATION: 120 // minutes
} as const;

// Auto-save Configuration
export const AUTO_SAVE = {
  DEBOUNCE_DELAY: 1000, // milliseconds
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000 // milliseconds
} as const;

// UI Constants
export const UI_CONSTANTS = {
  TOAST_DURATION: 3000,
  LOADING_SPINNER_DELAY: 200,
  ANIMATION_DURATION: 300,
  MODAL_Z_INDEX: 1000
} as const;

// Storage Keys
export const STORAGE_KEYS = {
  TEAM_ID: 'currentTeamId',
  USER_PREFERENCES: 'userPreferences',
  DRAFT_DATA: 'draftData',
  LAST_VISITED: 'lastVisited'
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_FAILED: 'Please check your input and try again.',
  SAVE_FAILED: 'Failed to save changes. Please try again.',
  LOAD_FAILED: 'Failed to load data. Please refresh the page.',
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.'
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully',
  CREATED: 'Created successfully',
  UPDATED: 'Updated successfully',
  DELETED: 'Deleted successfully',
  IMPORTED: 'Data imported successfully',
  EXPORTED: 'Data exported successfully'
} as const;

// Default Values
export const DEFAULTS = {
  ROSTER_SIZE: {
    Football: 22,
    FlagFootball: 14,
    Basketball: 12,
    Soccer: 18,
    Baseball: 15,
    Softball: 15,
    Volleyball: 12,
    Hockey: 20,
    Lacrosse: 20,
    Tennis: 12
  },
  FORMATION: {
    OFFENSIVE: 'Spread',
    DEFENSIVE: '4-3'
  },
  PRACTICE_LOCATION: 'Main Field',
  AGE_LEVEL: '13U',
  EXPERIENCE_LEVEL: 'Intermediate'
} as const;

// Regex Patterns
export const REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[\d\s\-()]{10,}$/,
  JERSEY_NUMBER: /^\d{1,3}$/,
  TIME_24H: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
  TIME_12H: /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i
} as const;

// File Upload Constants
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
} as const;

// Pagination Constants
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 5
} as const;

// Color Scheme
export const COLORS = {
  PRIMARY: '#3B82F6',
  SECONDARY: '#6B7280',
  SUCCESS: '#10B981',
  WARNING: '#F59E0B',
  ERROR: '#EF4444',
  INFO: '#06B6D4'
} as const;

// Breakpoints (for responsive design)
export const BREAKPOINTS = {
  SM: '640px',
  MD: '768px',
  LG: '1024px',
  XL: '1280px',
  '2XL': '1536px'
} as const; 